import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { LIBRARY_VERSION } from "../version.mjs";
import { getCssPrefix } from "./styles/css-prefix.mjs";
export function nodeIsString(node) {
  return node && typeof node.valueOf() === 'string';
}

/**
 * Combines classnames with a hashed encore prefix, as needed
 *
 * @param prefixedClasses - A list to prefix, either of classnames or of objects where the keys are classes and the values are bools indicating whether to include the class.
 * @param unprefixedClasses - A classname or list of classnames for which the prefix should be skipped (ex: className passed by customer)
 * @returns A single string of classnames, including a hashed prefix when relevant
 */
export var ecx = function ecx(prefixedClasses, unprefixedClasses) {
  // import hash from generated file
  var prefix = getCssPrefix(LIBRARY_VERSION);

  // this list should match the postcss prefixer settings in generate-component-css.ts
  // TODO: move this list to a location shared between the build process and library runtime
  var prefixesToSkip = ['encore-', 'encore-internal-'];
  var classes = [];
  var passthroughClasses = [unprefixedClasses];
  var shouldSkipPrefix = function shouldSkipPrefix(className) {
    return prefixesToSkip.some(function (pre) {
      return className.startsWith(pre);
    });
  };
  prefixedClasses.forEach(function (arg) {
    if (typeof arg === 'string' && arg) {
      if (shouldSkipPrefix(arg)) {
        classes.push(arg);
      } else {
        classes.push("".concat(prefix).concat(arg));
      }
    } else if (typeof arg === 'object' && arg !== null) {
      Object.keys(arg).forEach(function (key) {
        if (arg[key]) {
          if (shouldSkipPrefix(key)) {
            classes.push(key);
          } else {
            classes.push("".concat(prefix).concat(key));
          }
        }
      });
    }
  });
  if (Array.isArray(unprefixedClasses)) {
    // if unprefixedClasses need processing, wipe out what we've stored in there so far,
    // then re-add as we transform objects into strings
    passthroughClasses = [];
    unprefixedClasses.forEach(function (arg) {
      if (typeof arg === 'string' && arg) {
        passthroughClasses.push(arg);
      } else if (typeof arg === 'object' && arg !== null) {
        Object.keys(arg).forEach(function (key) {
          if (arg[key]) {
            passthroughClasses.push(key);
          }
        });
      }
    });
  }
  return [].concat(classes, _toConsumableArray(passthroughClasses)).join(' ').trim();
};
export function renderConfigOrCustom(node, DefaultEl) {
  if (nodeIsString(node)) {
    return DefaultEl;
  }
  return node;
}
export var isWindowDefined = function isWindowDefined() {
  return typeof window !== 'undefined';
};
export var capitalize = function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
export function isLtr(ref) {
  var direction = 'ltr';
  if (ref.current) {
    direction = window.getComputedStyle(ref.current).direction;
  }
  return direction === 'ltr';
}

/**
 * Merges multiple refs into a single ref callback.
 * The merged ref callback can be used to assign a value to multiple refs simultaneously.
 * Heavily inspired by https://github.com/gregberge/react-merge-refs
 *
 * @template T - The type of the HTML element.
 * @param refs - An array of refs to be merged.
 * @returns A ref callback that assigns a value to all the merged refs.
 */

export function mergeRefs(refs) {
  return function (value) {
    refs.forEach(function (ref) {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null) {
        ref.current = value;
      }
    });
  };
}

/**
 * Helper for converting a token (in string px format) to an integer.
 * @example
 * pxToInt(spacer16) // returns 16
 *  */
export var pxToInt = function pxToInt(px) {
  return parseInt(px, 10);
};
var textVariantMapping = {
  bass: 'headlineLarge',
  forte: 'headlineMedium',
  brio: 'titleLarge',
  altoBrio: 'titleLarge',
  alto: 'titleMedium',
  canon: 'titleSmall',
  celloCanon: 'titleSmall',
  cello: 'bodyMediumBold',
  ballad: 'bodyMedium',
  balladBold: 'bodyMediumBold',
  viola: 'bodySmall',
  violaBold: 'bodySmallBold',
  mesto: 'bodySmall',
  mestoBold: 'bodySmallBold',
  finale: 'marginal',
  finaleBold: 'marginalBold',
  // variants below aren't 1:1 so we're mapping to the variant that preserves visual space (they've also been deprecated for ~5 years)
  // https://www.figma.com/proto/KzUo98tnaOuGXNg3rrcYB8/Font-requests?page-id=2205%3A4688&node-id=2646-5935&viewport=409%2C106%2C0.05&scaling=min-zoom&starting-point-node-id=2645%3A5754
  metronome: 'bodySmallBold',
  minuet: 'marginal',
  minuetBold: 'marginalBold'
};

/** Variant map used if the weight prop (deprecated) is used to request a bold or black weight. */
var textVariantMappingWithBold = {
  cello: 'bodyMediumBold',
  ballad: 'bodyMediumBold',
  viola: 'bodySmallBold',
  mesto: 'bodySmallBold',
  finale: 'marginalBold',
  metronome: 'bodySmallBold',
  minuet: 'marginalBold'
};

/** Takes a legacy Type variant and and weight returns the equivalent Text variant */
export function mapLegacyToTextVariant(variant, weight) {
  // If the variant is a valid Text variant, return it (we ignore the `weight` prop in this case).
  if (Object.values(textVariantMapping).includes(variant)) {
    return variant;
  }
  // Logic for handling the `weight` prop:
  // - weight="bold" or "black" are treated the same, we map to the bold or black variant at the same font size
  // - No weight or weight="book" -> return the new variant mapping
  if ((weight === 'black' || weight === 'bold') && Object.keys(textVariantMappingWithBold).includes(variant)) {
    return textVariantMappingWithBold[variant];
  }
  return textVariantMapping[variant];
}